import { useEffect, useMemo, useState } from 'react';

import {
  answerPayload,
  answeredCount,
  blankDraft,
  chooseOption,
  chooseOther,
  isComplete,
  pendingQuestions,
  setOtherText,
} from '../pages/agentQuestions';
import './AgentQuestionCard.css';

// The requirements round: everything the agent could not settle for itself, asked
// once, as one card with a section per question.
//
// It exists because the orchestrator used to be told that one clarifying question was
// the maximum, so it invented the three things that decide what a workflow MEANS —
// which part, which material, which faces carry the load — and produced a
// healthy-looking result for the wrong problem. Four sequential questions would have
// been worse than guessing; one card is not.
//
// **It owns its stylesheet.** It is rendered from two pages (the dashboard thread and
// the project editor's ChatPanel), and CRA bundles a stylesheet only when something in
// the loaded chunk imports it — the `AddQualityCheckModal` trap, where every rule went
// missing on whichever page had not been visited yet. `panelStylesheets.test.js` pins
// this import.
//
// Amber `#d97706` for the heading, matching `AgentParkedBanner`, `TileStatusDot` and
// `GroupFrameNode`, so "waiting on you" is one colour wherever it appears.

export default function AgentQuestionCard({ run, onSubmit, disabled = false }) {
  const questions = useMemo(() => pendingQuestions(run), [run]);
  const [draft, setDraft] = useState(() => blankDraft(questions));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // Re-seed when the ROUND changes, not on every poll. The 2 s status poll returns a
  // fresh array object each time, so a `[questions]` dependency would rebuild the
  // draft under the user's fingers and lose what they had typed. The id list is the
  // identity of the round.
  const ids = questions.map((q) => q.questionId).join('|');
  useEffect(() => {
    setDraft(blankDraft(questions));
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  if (!questions.length) return null;

  const update = (questionId, next) => setDraft((d) => ({ ...d, [questionId]: next }));

  async function send({ skipped }) {
    if (sending) return;
    setSending(true);
    setError(null);
    try {
      await onSubmit({
        answers: skipped ? [] : answerPayload(questions, draft),
        skipped,
      });
    } catch (err) {
      // Kept on screen with the draft intact: a failed submit that cleared the form
      // would make the user retype every answer.
      setError(err?.message || String(err));
    } finally {
      setSending(false);
    }
  }

  const answered = answeredCount(questions, draft);
  const complete = isComplete(questions, draft);
  const busy = sending || disabled;

  return (
    <section className="agent-questions" aria-label="Questions from the assistant">
      <header className="agent-questions-head">
        <span className="agent-questions-glyph" aria-hidden="true">
          ?
        </span>
        <div>
          <h3 className="agent-questions-title">Before I plan — a few things</h3>
          <p className="agent-questions-sub">
            Nothing has been built yet. Answer what you care about; I&apos;ll choose sensible
            defaults for the rest and mark them so you can see what I assumed.
          </p>
        </div>
      </header>

      <ol className="agent-questions-list">
        {questions.map((q) => {
          const entry = draft[q.questionId] || { selected: [], other: '', useOther: false };
          const name = `q-${q.questionId}`;
          return (
            <li key={q.questionId} className="agent-question">
              <div className="agent-question-head">
                <span className="agent-question-chip">{q.header}</span>
                <span className="agent-question-text">{q.question}</span>
              </div>
              {q.why && <p className="agent-question-why">{q.why}</p>}

              <div className="agent-question-options" role="group" aria-label={q.header}>
                {(q.options || []).map((opt) => (
                  <label key={opt.label} className="agent-question-option">
                    <input
                      type={q.multiSelect ? 'checkbox' : 'radio'}
                      name={name}
                      disabled={busy}
                      checked={!entry.useOther && (entry.selected || []).includes(opt.label)}
                      onChange={() =>
                        update(q.questionId, chooseOption(entry, opt.label, q.multiSelect))
                      }
                    />
                    <span className="agent-question-option-body">
                      <span className="agent-question-option-label">{opt.label}</span>
                      {opt.description && (
                        <span className="agent-question-option-desc">{opt.description}</span>
                      )}
                    </span>
                  </label>
                ))}

                {/* Free text, unless the model said the options are the whole set —
                    a file that must come from the repository, say. Typing into it
                    selects it (see `setOtherText`), or an answer typed without
                    clicking the control would be silently dropped. */}
                {q.allowOther !== false && (
                  <label className="agent-question-option agent-question-other">
                    <input
                      type={q.multiSelect ? 'checkbox' : 'radio'}
                      name={name}
                      disabled={busy}
                      checked={!!entry.useOther}
                      onChange={() => update(q.questionId, chooseOther(entry, !entry.useOther))}
                    />
                    <span className="agent-question-option-body">
                      <span className="agent-question-option-label">Something else</span>
                      <input
                        type="text"
                        className="agent-question-input"
                        value={entry.other || ''}
                        disabled={busy}
                        placeholder="Type your answer…"
                        onChange={(e) => update(q.questionId, setOtherText(entry, e.target.value))}
                      />
                    </span>
                  </label>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {error && <p className="agent-questions-error">{error}</p>}

      <footer className="agent-questions-actions">
        <button
          type="button"
          className="agent-questions-submit"
          disabled={busy}
          onClick={() => send({ skipped: false })}
        >
          {sending ? 'Sending…' : complete ? 'Submit' : `Submit ${answered}/${questions.length}`}
        </button>
        <button
          type="button"
          className="agent-questions-skip"
          disabled={busy}
          title="Carry on without answering: I'll use defaults and flag every value I guessed"
          onClick={() => send({ skipped: true })}
        >
          Skip — you decide
        </button>
      </footer>
    </section>
  );
}
